// components/Sidebar.js - Updated with Dashboard and Wallet History tabs for agents
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContext";
import {
  User,
  Calendar,
  CreditCard,
  Building,
  LogOut,
  BarChart3,
  Wallet,
  MapPin,
} from "lucide-react";

const Sidebar = ({ activeTab, setActiveTab }) => {
  const {
    user,
    userType,
    logout,
    getUserDisplayName,
    getUserFirstName,
    getUserFullName,
    getUserInitials,
    getTimeBasedGreeting,
  } = useContext(AuthContext);
  const navigate = useNavigate();

  const formatMobile = (mobile) => {
    if (!mobile) return "";
    // Remove country code and format
    const cleaned = mobile.replace(/^\+?91/, "").replace(/\D/g, "");
    if (cleaned.length >= 10) {
      return `+91 ${cleaned.slice(-10, -5)} ${cleaned.slice(-5)}`;
    }
    return mobile;
  };

  const handleLogout = () => {
    logout();
    navigate("/"); // Redirect to home page
  };

  // Determine if user is an agent
  const isAgent = () => {
    if (!user) return false;
    return (
      user.role === "agent" ||
      user.userType === "agent" ||
      user.type === "agent" ||
      userType === "agent"
    );
  };

  // If no user is logged in, show login prompt with clean design
  if (!user) {
    return (
      <div className="h-screen w-64 bg-white border-r border-gray-200 flex flex-col justify-center items-center px-8">
        <div className="text-center text-gray-600">
          <div className="w-20 h-20 mx-auto rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-2xl font-bold mb-6">
            ?
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-6">
            Please Login
          </h3>
          <button
            onClick={() => navigate("/")}
            className="w-full bg-brand-600 hover:bg-brand-700 text-white py-3 px-6 rounded-lg transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Define menu items based on user type
  const getMenuItems = () => {
    const baseMenuItems = [
      { id: "profile", label: "Profile", icon: User },
      { id: "bookings", label: "Booking", icon: Calendar },
      { id: "marketplace", label: "Taxi & Package Trips", icon: MapPin },
      { id: "payment-history", label: "Payment History", icon: CreditCard },
    ];

    if (isAgent()) {
      // Agent menu items in specific order
      return [
        { id: "dashboard", label: "Dashboard", icon: BarChart3 },
        ...baseMenuItems,
        { id: "wallet-history", label: "Wallet History", icon: Wallet },
        { id: "bank-details", label: "Bank Details", icon: Building },
      ];
    }

    return baseMenuItems;
  };

  const menuItems = getMenuItems();

  return (
    <div className="h-100 w-80 bg-white border-r border-gray-200 flex flex-col">
      {/* User Profile Section */}
      <div className="px-6 py-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-bold text-lg shrink-0">
            {getUserInitials ? getUserInitials() : "U"}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-400">{getTimeBasedGreeting ? getTimeBasedGreeting() : "Hello"}</p>
            <p className="font-semibold text-gray-900 truncate">
              {getUserFullName ? getUserFullName() : getUserDisplayName?.() || "User"}
            </p>
            {user?.mobile && <p className="text-xs text-gray-400">{formatMobile(user.mobile)}</p>}
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-3 px-3 space-y-1">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-colors duration-200 ${
                isActive
                  ? "bg-brand-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <IconComponent className="w-[18px] h-[18px] shrink-0" />
              <span className="font-medium text-sm">{item.label}</span>
              {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-white" />}
            </button>
          );
        })}
      </nav>

      {/* Bottom Section with Logout */}
      <div className="border-t border-gray-100 p-3">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
        >
          <LogOut className="w-[18px] h-[18px]" />
          <span className="font-medium text-sm">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
